import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import DashboardPage from './DashboardPage';
import { getBetaUser, setBetaUser, type BetaRole, type BetaUser } from '@/lib/betaAuth';
import { signInWithEmailPassword, createUserWithEmailPassword, startPhoneSignIn, confirmPhoneCode, signInWithGoogle, signOut as firebaseSignOut, sendPasswordReset, clearBackupAuthState } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { loadUserProfile } from '@/lib/userProfile';
import { isPaulEmail } from '@/lib/paulAccess';

// Map Firebase auth error codes to friendly user-facing messages
const getAuthErrorMessage = (error: unknown): string => {
  if (!(error instanceof Error)) return 'An unexpected error occurred.';
  const msg = error.message;
  if (msg.includes('auth/user-not-found') || msg.includes('invalid-credential') || msg.includes('wrong-password')) {
    return 'Email or password is incorrect. Please check and try again, or sign up for a new account.';
  }
  if (msg.includes('auth/email-already-in-use')) return 'This email is already registered. Try signing in instead.';
  if (msg.includes('auth/weak-password')) return 'Password is too weak. Use at least 6 characters.';
  if (msg.includes('auth/invalid-email')) return 'Please enter a valid email address.';
  if (msg.includes('auth/operation-not-allowed')) return 'This sign-in method is not enabled. Contact support.';
  if (msg.includes('invalid-phone-number')) return 'Invalid phone number. Use a valid international format.';
  if (msg.includes('appCheck/recaptcha-error')) return 'reCAPTCHA failed. Please try again or switch to email sign-in.';
  if (msg.includes('firebase-app-check-token-is-invalid')) return 'App Check token failed. Refresh the page and try again.';
  if (msg.includes('auth/network-request-failed')) return 'Network error. Please check your connection and try again.';
  if (msg.includes('auth/too-many-requests')) return 'Too many sign-in attempts. Please try again later.';
  return msg;
};

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getAuthInstance } from '@/lib/firebase';
import type { User } from 'firebase/auth';

const waitForAuthUser = async (timeoutMs = 10000): Promise<User | null> => {
  const auth = await getAuthInstance();
  if (!auth) return null;

  if (auth.currentUser) {
    console.log('[Beta] Auth user found immediately');
    return auth.currentUser;
  }

  const authWithReady = auth as User['auth'] & { authStateReady?: () => Promise<void> };
  if (typeof authWithReady.authStateReady === 'function') {
    try {
      await Promise.race([
        authWithReady.authStateReady(),
        new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
      ]);
      if (auth.currentUser) {
        console.log('[Beta] Auth user found after authStateReady');
        return auth.currentUser;
      }
    } catch {
      // Fall through to onAuthStateChanged
    }
  }

  if (typeof sessionStorage !== 'undefined') {
    const backupUid = sessionStorage.getItem('firebase-auth-uid-backup');
    if (backupUid && !auth.currentUser) {
      console.log('[Beta] Found backup auth in sessionStorage, waiting for Firebase to load...');
    }
  }

  const { onAuthStateChanged } = await import('firebase/auth');
  return await new Promise<User | null>((resolve) => {
    let settled = false;
    const timeoutId = setTimeout(() => {
      if (settled) return;
      settled = true;
      unsubscribe();
      console.log('[Beta] Auth wait timeout, currentUser:', !!auth.currentUser);
      resolve(auth.currentUser ?? null);
    }, timeoutMs);
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      unsubscribe();
      console.log('[Beta] Auth state changed:', !!user);
      resolve(user ?? null);
    });
  });
};

const Beta = () => {
  const initialStoredUserRef = useRef<BetaUser | null | undefined>(undefined);
  if (initialStoredUserRef.current === undefined) {
    const stored = getBetaUser();
    console.log('[Beta] Initial getBetaUser():', stored ? `${stored.email} (${stored.role})` : 'NULL');
    initialStoredUserRef.current = stored;
  }

  const [user, setUser] = useState<BetaUser | null>(() => {
    return initialStoredUserRef.current ?? null;
  });
  const [authSessionReady, setAuthSessionReady] = useState(() => initialStoredUserRef.current === null);
  const [signInData, setSignInData] = useState({ email: '', password: '', role: 'viewer' as BetaRole });
  const [signUpData, setSignUpData] = useState({ name: '', email: '', password: '', role: 'viewer' as BetaRole });
  const [phoneData, setPhoneData] = useState({ phone: '', code: '' });
  const [phoneConfirmation, setPhoneConfirmation] = useState<any | null>(null);
  const [phoneSending, setPhoneSending] = useState(false);
  const [phoneVerifying, setPhoneVerifying] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugState, setDebugState] = useState<any>(null);
  const [googleSigningIn, setGoogleSigningIn] = useState(false);

  useEffect(() => {
    let isActive = true;

    (async () => {
      const hadStoredUser = Boolean(initialStoredUserRef.current);
      try {
        const authUser = await waitForAuthUser(hadStoredUser ? 15000 : 2500);
        if (!isActive) return;

        if (!authUser) {
          clearBackupAuthState();
          if (hadStoredUser) {
            try { setBetaUser(null); } catch {}
            setUser(null);
          }
          return;
        }

        let profile: Awaited<ReturnType<typeof loadUserProfile>> | null = null;
        try {
          profile = await loadUserProfile(authUser.uid);
        } catch (error) {
          console.warn('[Beta] Failed to load profile during session restore:', error);
        }
        if (!isActive) return;

        const resolvedEmail = authUser.email ?? profile?.email ?? '';
        const role: BetaRole =
          isPaulEmail(resolvedEmail) || profile?.roles?.includes('admin') || profile?.role === 'admin'
            ? 'admin'
            : 'viewer';
        const nextUser: BetaUser = {
          name: profile?.displayName ?? authUser.displayName ?? undefined,
          email: resolvedEmail,
          role,
        };

        setBetaUser(nextUser);
        setUser(nextUser);
      } catch (error) {
        if (!isActive) return;
        console.error('[Beta] Session validation failed:', error);
        clearBackupAuthState();
        if (initialStoredUserRef.current) {
          try { setBetaUser(null); } catch {}
          setUser(null);
        }
      } finally {
        if (isActive) {
          setAuthSessionReady(true);
        }
      }
    })();

    return () => {
      isActive = false;
    };
  }, []);

  // clean up any RecaptchaVerifier instance when this page unmounts to avoid
  // browser actor/destroyed errors in some environments
  useEffect(() => {
    return () => {
      try {
        // @ts-ignore
        if (typeof window !== 'undefined' && (window as any).recaptchaVerifierInstance) {
          try { (window as any).recaptchaVerifierInstance.clear(); } catch {}
          try { (window as any).recaptchaVerifierInstance = null; } catch {}
        }
      } catch {}
    };
  }, []);

  useEffect(() => {
    if (!debugOpen) return;
    let mounted = true;
    (async () => {
      try {
        const auth = await getAuthInstance();
        const win = typeof window !== 'undefined' ? (window as any) : undefined;
        const verifier = win?.recaptchaVerifierInstance;
        const appCheckDebugToken = win?.FIREBASE_APPCHECK_DEBUG_TOKEN || (typeof localStorage !== 'undefined' ? localStorage.getItem('firebase:appCheckDebugToken') : null);
        const info = {
          verifierExists: Boolean(verifier),
          verifierHasAuth: Boolean(verifier?._auth),
          verifierAuthMatchesCurrent: verifier?._auth === auth,
          verifierDebug: verifier ? { _authType: typeof verifier._auth, _authTenant: verifier._auth?.tenantId ?? null } : null,
          currentAuth: auth ? { tenantId: (auth as any).tenantId ?? null, currentUserId: auth.currentUser?.uid ?? null } : null,
          appCheckDebugToken: appCheckDebugToken ? `${appCheckDebugToken.substring(0, 8)}...` : null,
        };
        if (mounted) setDebugState(info);
      } catch (e) {
        if (mounted) setDebugState({ error: String(e) });
      }
    })();
    return () => { mounted = false; };
  }, [debugOpen]);

  const finalizeAuthenticatedUser = async (
    fbUser: User,
    fallbackRole: BetaRole,
    fallbackName?: string,
  ): Promise<{ authUser: User; profile: Awaited<ReturnType<typeof loadUserProfile>> | null; nextUser: BetaUser }> => {
    const authUser = (await waitForAuthUser(10000)) ?? fbUser;
    try {
      await authUser.getIdToken();
    } catch {}

    let profile: Awaited<ReturnType<typeof loadUserProfile>> | null = null;
    if (authUser.uid) {
      try {
        profile = await loadUserProfile(authUser.uid);
      } catch (error) {
        console.warn('[Beta] Failed to load profile after auth handoff:', error);
      }
    }

    const resolvedEmail = authUser.email ?? profile?.email ?? fbUser.email ?? '';
    const resolvedRole: BetaRole =
      isPaulEmail(resolvedEmail) || profile?.roles?.includes('admin') || profile?.role === 'admin'
        ? 'admin'
        : fallbackRole;
    const nextUser: BetaUser = {
      name: profile?.displayName ?? authUser.displayName ?? fallbackName ?? undefined,
      email: resolvedEmail,
      role: resolvedRole,
    };

    setBetaUser(nextUser);
    setUser(nextUser);

    return { authUser, profile, nextUser };
  };

  const handleSignIn = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const email = signInData.email.trim();
    const password = signInData.password || '';
    if (!email || !password) return alert('Please provide email and password');
    
    console.log('[Beta] Sign-in attempt:', email);
    
    try {
      console.log('[Beta] Calling signInWithEmailPassword');
      const fbUser = await signInWithEmailPassword(email, password);
      console.log('[Beta] Sign-in successful:', fbUser.uid);

      const { nextUser } = await finalizeAuthenticatedUser(
        fbUser,
        signInData.role,
      );
      console.log('[Beta] User state updated:', nextUser);
      // If sent here from a guarded route (e.g. /beta?next=/timeline-miner), return there now
      // that we're authenticated. Full navigation so the fresh auth session is applied. Only
      // allow internal same-origin paths (guard against open redirect).
      try {
        const nextParam = new URLSearchParams(window.location.search).get('next');
        if (nextParam && nextParam.startsWith('/') && !nextParam.startsWith('//')) {
          window.location.assign(nextParam);
        }
      } catch { /* ignore */ }
    } catch (err) {
      console.error('[Beta] Sign-in failed:', err);
      const msg = getAuthErrorMessage(err);
      alert('Sign-in failed: ' + msg);
    }
  };

  const handleSignUp = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const email = signUpData.email.trim();
    const password = signUpData.password || '';
    if (!email || !password) return alert('Please provide email and password');
    try {
      const created = await createUserWithEmailPassword(email, password);
      await finalizeAuthenticatedUser(
        created,
        signUpData.role,
        signUpData.name?.trim() || undefined,
      );
    } catch (err) {
      console.error('Sign-up failed', err);
      const msg = getAuthErrorMessage(err);
      alert('Sign-up failed: ' + msg);
    }
  };

  const handleForgotPassword = async () => {
    const email = signInData.email.trim();
    if (!email) {
      alert('Enter your email in the sign-in form, then tap Forgot password.');
      return;
    }
    try {
      setResettingPassword(true);
      await sendPasswordReset(email);
      alert('Password reset email sent. Check your inbox.');
    } catch (err) {
      const msg = getAuthErrorMessage(err);
      alert('Unable to send reset email: ' + msg);
    } finally {
      setResettingPassword(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setGoogleSigningIn(true);
      const fbUser = await signInWithGoogle();

      await finalizeAuthenticatedUser(fbUser, 'viewer');
    } catch (err: any) {
      console.error('Google sign-in failed', err);
      // Check for specific error codes
      if (err.code === 'auth/popup-closed-by-user') {
        // User closed popup without completing sign-in, don't show error
        return;
      }
      if (err.code === 'auth/operation-not-allowed') {
        alert('Google sign-in is not enabled. Please contact support or use email/phone sign-in.');
        return;
      }
      if (err.code === 'auth/cancelled-popup-request') {
        // User cancelled, don't show error
        return;
      }
      const msg = getAuthErrorMessage(err);
      alert('Google sign-in failed: ' + msg);
    } finally {
      setGoogleSigningIn(false);
    }
  };

  const handleSignOut = async () => {
    setBetaUser(null);
    setUser(null);
    setPhoneConfirmation(null);
    setPhoneData({ phone: '', code: '' });
    try {
      await firebaseSignOut();
    } catch {}
  };

  if (!authSessionReady) {
    return (
      <div className="min-h-screen bg-background nebula-bg star-field flex items-center justify-center px-4">
        <div className="text-center space-y-3">
          <p className="text-sm uppercase tracking-[0.3em] text-muted-foreground">Loading</p>
          <h1 className="text-2xl font-display font-semibold">Checking beta access…</h1>
        </div>
      </div>
    );
  }

  // REMOVED: Beta access gates - always allow access
  /*
  if (!betaEnabled) {
    return (
      <div className="min-h-screen bg-background nebula-bg star-field flex items-center justify-center px-4">
        <div className="max-w-lg text-center space-y-4 bg-card/70 border border-border/60 rounded-2xl p-8 backdrop-blur">
          <p className="text-sm uppercase tracking-[0.3em] text-muted-foreground">Beta Locked</p>
          <h1 className="text-2xl font-display font-semibold">Beta access is currently disabled.</h1>
          <p className="text-sm text-muted-foreground">
            Ask an administrator to enable beta access from the developer page before signing in.
          </p>
          <Button asChild variant="outline">
            <Link to="/">Back to landing</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (!betaDashboardsVisible) {
    return (
      <div className="min-h-screen bg-background nebula-bg star-field flex items-center justify-center px-4">
        <div className="max-w-lg text-center space-y-4 bg-card/70 border border-border/60 rounded-2xl p-8 backdrop-blur">
          <p className="text-sm uppercase tracking-[0.3em] text-muted-foreground">Dashboards Hidden</p>
          <h1 className="text-2xl font-display font-semibold">Beta dashboards are not shared yet.</h1>
          <p className="text-sm text-muted-foreground">
            Admins can enable global dashboard visibility for beta users from the developer page.
          </p>
          <Button asChild variant="outline">
            <Link to="/">Back to landing</Link>
          </Button>
        </div>
      </div>
    );
  }
  */

  if (user) {
    console.log('[Beta] Rendering dashboard for user:', user?.email || 'unknown');
    return <DashboardPage />;
  }

  return (
    <div className="min-h-screen bg-background nebula-bg star-field flex items-center justify-center px-4 py-12">
      {/* Recaptcha container for invisible reCAPTCHA (created once and hidden) */}
      <div id="recaptcha" style={{ position: 'absolute', left: -9999, top: -9999, width: 1, height: 1 }} />
      <div className="w-full max-w-3xl space-y-8">
        <div className="text-center space-y-3">
          <p className="text-sm uppercase tracking-[0.3em] text-muted-foreground">Beta Access</p>
          <h1 className="text-3xl md:text-4xl font-display font-bold text-cosmic-gradient">
            Sign in to the beta dashboards
          </h1>
          <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
            Use the beta path to sign in or create a beta account. Admin access is reserved for users
            with the admin role.
          </p>
        </div>

        {/* App Check debug status */}
        <div className="text-center text-xs text-muted-foreground mb-4">
          App Check: {import.meta.env.VITE_ENABLE_APPCHECK === '1' ? '✓ enabled' : '✗ disabled'}
          {import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN && ' (debug token active)'}
        </div>

        <div className="rounded-3xl border border-border/60 bg-card/70 backdrop-blur p-6">
          <Tabs defaultValue="sign-in" className="space-y-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="sign-in">Sign in</TabsTrigger>
              <TabsTrigger value="sign-up">Sign up</TabsTrigger>
              <TabsTrigger value="phone">Phone</TabsTrigger>
            </TabsList>

            <TabsContent value="sign-in" className="space-y-6">
              <form className="space-y-4" onSubmit={handleSignIn} autoComplete="on">
                <div className="space-y-2">
                  <Label htmlFor="beta-email">Email</Label>
                  <Input
                    id="beta-email"
                    name="email"
                    type="email"
                    value={signInData.email}
                    onChange={(event) =>
                      setSignInData(prev => ({ ...prev, email: event.target.value }))
                    }
                    placeholder="you@studio.com"
                    autoComplete="username"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="beta-password">Password</Label>
                  <Input
                    id="beta-password"
                    name="password"
                    type="password"
                    value={signInData.password}
                    onChange={(event) => setSignInData(prev => ({ ...prev, password: event.target.value }))}
                    placeholder="Enter password"
                    autoComplete="current-password"
                    required
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={resettingPassword}
                    className="text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground disabled:opacity-60"
                  >
                    {resettingPassword ? 'Sending reset…' : 'Forgot password?'}
                  </button>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="beta-role">Role</Label>
                  <Select
                    value={signInData.role}
                    onValueChange={(value) =>
                      setSignInData(prev => ({ ...prev, role: value as BetaRole }))
                    }
                  >
                    <SelectTrigger id="beta-role" className="bg-card/80">
                      <SelectValue placeholder="Select a role" />
                    </SelectTrigger>
                      <SelectContent className="bg-card/90">
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full">
                  Enter beta
                </Button>
              </form>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border/30" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">Or</span>
                </div>
              </div>

              <Button
                onClick={handleGoogleSignIn}
                disabled={googleSigningIn}
                variant="outline"
                className="w-full justify-center gap-3 rounded-full border border-border/70 bg-white text-black shadow-sm hover:shadow focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#4285F4]"
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-sm overflow-hidden" aria-hidden>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19.6 10.23c0-.64-.06-1.25-.17-1.84H10v3.48h5.37a4.58 4.58 0 01-1.99 3.01v2.5h3.22c1.89-1.74 2.99-4.31 2.99-7.15z" fill="#4285F4"/>
                    <path d="M10 20c2.7 0 4.96-.89 6.61-2.41l-3.22-2.5c-.9.6-2.06.96-3.39.96-2.61 0-4.82-1.76-5.61-4.12H1.07v2.59A10 10 0 0010 20z" fill="#34A853"/>
                    <path d="M4.39 11.93A5.99 5.99 0 014 10c0-.67.12-1.33.35-1.93V5.48H1.07A10 10 0 000 10c0 1.62.39 3.16 1.07 4.52l3.32-2.59z" fill="#FBBC05"/>
                    <path d="M10 3.98c1.47 0 2.79.51 3.82 1.51l2.86-2.86C14.95.98 12.7 0 10 0 6.1 0 2.72 2.23 1.07 5.48l3.28 2.59C5.18 5.74 7.39 3.98 10 3.98z" fill="#EA4335"/>
                  </svg>
                </span>
                <span className="text-sm font-semibold tracking-tight">
                  {googleSigningIn ? 'Signing in…' : 'Sign in with Google'}
                </span>
              </Button>
            </TabsContent>

            <TabsContent value="phone" className="space-y-6">
              <form className="space-y-4" onSubmit={async (e) => { e.preventDefault();
                const phone = phoneData.phone.trim();
                if (!phone) return alert('Please provide a phone number');
                try {
                  setPhoneSending(true);
                  
                  const confirmation = await startPhoneSignIn(phone, 'recaptcha');
                  setPhoneConfirmation(confirmation);
                  setPhoneData(prev => ({ ...prev, code: '' }));
                  alert('SMS sent — enter the code to continue');
                } catch (err) {
                  console.error('SMS send failed', err);
                  const msg = getAuthErrorMessage(err);
                  alert('SMS send failed: ' + msg);
                } finally { setPhoneSending(false); }
              }} autoComplete="on">
                <div className="space-y-2">
                  <Label htmlFor="beta-phone">Phone</Label>
                  <Input
                    id="beta-phone"
                    name="tel"
                    type="tel"
                    value={phoneData.phone}
                    onChange={(event) => setPhoneData(prev => ({ ...prev, phone: event.target.value }))}
                    placeholder="+1 555-555-5555"
                    autoComplete="tel"
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={phoneSending}>{phoneSending ? 'Sending…' : 'Send SMS'}</Button>
              </form>

              {phoneConfirmation && (
                <form className="space-y-4" onSubmit={async (e) => {
                  e.preventDefault();
                  const code = phoneData.code.trim();
                  if (!code) return alert('Enter verification code');
                  try {
                    setPhoneVerifying(true);
                    const user = await confirmPhoneCode(phoneConfirmation, code);
                    const nextUser: BetaUser = { email: user.phoneNumber ?? phoneData.phone, role: signInData.role };
                    setBetaUser(nextUser);
                    setUser(nextUser);
                  } catch (err) {
                    console.error('Code confirm failed', err);
                    const msg = getAuthErrorMessage(err);
                    alert('Code confirmation failed: ' + msg);
                  } finally { setPhoneVerifying(false); }
                }} autoComplete="off">
                  <div className="space-y-2">
                    <Label htmlFor="beta-code">Code</Label>
                    <Input
                      id="beta-code"
                      name="one-time-code"
                      value={phoneData.code}
                      onChange={(e) => setPhoneData(prev => ({ ...prev, code: e.target.value }))}
                      placeholder="123456"
                      autoComplete="one-time-code"
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={phoneVerifying}>{phoneVerifying ? 'Verifying…' : 'Confirm code'}</Button>
                </form>
              )}
            </TabsContent>

            <TabsContent value="sign-up" className="space-y-6">
              <form className="space-y-4" onSubmit={handleSignUp} autoComplete="on">
                <div className="space-y-2">
                  <Label htmlFor="beta-name">Name</Label>
                  <Input
                    id="beta-name"
                    name="name"
                    value={signUpData.name}
                    onChange={(event) =>
                      setSignUpData(prev => ({ ...prev, name: event.target.value }))
                    }
                    placeholder="Nova Sterling"
                    autoComplete="name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="beta-email-signup">Email</Label>
                  <Input
                    id="beta-email-signup"
                    name="email"
                    type="email"
                    value={signUpData.email}
                    onChange={(event) =>
                      setSignUpData(prev => ({ ...prev, email: event.target.value }))
                    }
                    placeholder="you@studio.com"
                    autoComplete="email"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="beta-password-signup">Password</Label>
                  <Input
                    id="beta-password-signup"
                    name="password"
                    type="password"
                    value={signUpData.password}
                    onChange={(event) => setSignUpData(prev => ({ ...prev, password: event.target.value }))}
                    placeholder="Create a password"
                    autoComplete="new-password"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="beta-role-signup">Role</Label>
                  <Select
                    value={signUpData.role}
                    onValueChange={(value) =>
                      setSignUpData(prev => ({ ...prev, role: value as BetaRole }))
                    }
                  >
                    <SelectTrigger id="beta-role-signup" className="bg-card/80">
                      <SelectValue placeholder="Select a role" />
                    </SelectTrigger>
                    <SelectContent className="bg-card/90">
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full">
                  Create beta account
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>

        <div className="flex justify-center">
          <Button asChild variant="outline">
            <Link to="/landing">Back to landing</Link>
          </Button>
        </div>
        {/* Developer pocket-door debug panel (left-edge slide-in) */}
        {/* Tab (always visible) */}
        <div className="fixed left-0 bottom-6 z-60">
          <button
            aria-expanded={debugOpen}
            aria-controls="beta-debug-panel"
            onClick={() => setDebugOpen(d => !d)}
            className="-ml-2 rounded-r-lg bg-card/90 border border-border/60 px-3 py-2 shadow-md hover:bg-card/95 text-xs"
            style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
          >
            {debugOpen ? 'Close' : 'Debug'}
          </button>
        </div>

        {/* Sliding panel */}
        <div
          id="beta-debug-panel"
          className={`fixed left-0 bottom-6 z-50 transform transition-transform duration-300 ${debugOpen ? 'translate-x-0' : '-translate-x-full'}`}
          style={{ width: 320 }}
        >
          <div className="w-80 bg-card/80 border border-border/60 rounded-r-2xl p-3 shadow-lg backdrop-blur">
            <div className="flex items-start justify-between gap-2">
              <div className="text-sm font-medium">Runtime Debug</div>
              <div>
                <Button size="sm" variant="ghost" onClick={() => setDebugOpen(false)}>Close</Button>
              </div>
            </div>
            <div className="mt-3 text-xs max-h-64 overflow-auto">
              <pre className="whitespace-pre-wrap">{JSON.stringify(debugState, null, 2)}</pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Beta;
